import React, { memo, useCallback, useEffect } from 'react';
import { Map } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getChanged, isDeepChanged, getIn, unwrap } from '../../utils/ChangeSpy';
import getOptionValue from '../../utils/getOptionValue';
import * as TARGET from '../../actions/company/companyTarget';
import * as BUYER from '../../actions/company/buyer/marketing';
import config from '../../config';
import { getBirthYear } from '../../utils/yearAgeConverter';
import AutoComplete from '../AutoComplete';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

const prefixes = config.values.getIn(['contact', 'prefixes']);
const suffixes = config.values.getIn(['contact', 'suffixes']);

const handleGetValue = suggestion => suggestion?.value ?? '';

const copySaved = (body, saved, isAdvisor) => {
  const specials = ['advTitle', 'advWeb', 'company'];

  if (saved) {
    Object.keys(saved).forEach(key => {
      if (key !== 'pivot' && key !== 'prefix' && key !== 'suffix') {
        body[key] = saved[key];
      }
    });
    if (saved.pivot) {
      Object.keys(saved.pivot).forEach(key => {
        if (specials.indexOf(key) < 0) {
          body[key] = saved.pivot[key];
        }
      });

      if (!isAdvisor) {
        specials.forEach(key => {
          body[key] = '';
          saved.pivot[key] = '';
        });
      } else {
        specials.forEach(key => {
          if (saved.pivot[key]) {
            body[key] = saved.pivot[key];
          }
        });
      }
    }
  }
};

const EditContactPopupContainer = ({
  children,
  companyId,
  contact,
  contactInfo,
  inputErrors,
  loading,
  pathPrefix,
  prefixes: prefixesProp,
  suffixes: suffixesProp,
  addValidationError,
  closeValidationError,
  findTitles,
  handleUpdateCompany,
  revertUpdate,
  saveCompanyContact,
}) => {
  const context = usePopupContext();

  useEffect(() => {
    context.onClosePopup(() => {
      const field = pathPrefix.slice(0, -1);

      revertUpdate({ field });
    });
  }, []);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
      closeValidationError({
        field: `${pathPrefix}${field}`,
      });
    },
    [closeValidationError, pathPrefix],
  );

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      return handleUpdateCompany({
        name: `${pathPrefix}${name}`,
        value,
      });
    },
    [handleUpdateCompany, pathPrefix],
  );

  const handleValidate = useCallback(
    contactValue => {
      let isValid = true;
      const path = pathPrefix.substring(0, pathPrefix.length - 1);

      if (
        (contactValue.birthYear && !/^\d+$/i.test(contactValue.birthYear)) ||
        (contactValue.age && !/^\d+$/i.test(contactValue.age))
      ) {
        isValid = false;
        addValidationError({
          path,
          field: 'age',
          message: 'Age is not valid.',
        });
      }

      return isValid;
    },
    [addValidationError, pathPrefix],
  );

  const handleAgeHelper = useCallback(body => {
    if ('age' in body) {
      body.birthYear = parseInt(getBirthYear(body.age), 10);
    }
  }, []);

  const handleOwnershipPercentHelper = useCallback(body => {
    if (body.ownershipPercent === '') {
      body.ownershipPercent = null;
    }
  }, []);

  const handleSave = useCallback(() => {
    if (inputErrors.size === 0) {
      const contactId = contact.get('id');
      const isAdvisor = unwrap(contact.getIn(['pivot', 'title'], '')).toUpperCase() === 'ADVISOR';
      const saved = getChanged(contact);

      saved.fullName = unwrap(contact.get('fullName'));
      saved.firstName = unwrap(contact.get('firstName'));
      saved.lastName = unwrap(contact.get('lastName'));
      saved.nick = unwrap(contact.get('nick')) ?? '';
      saved.pronounce = unwrap(contact.get('pronounce')) ?? '';
      saved.advTitle = unwrap(contact.getIn(['pivot', 'advTitle'])) ?? '';
      saved.advWeb = unwrap(contact.getIn(['pivot', 'advWeb'])) ?? '';
      saved.company = unwrap(contact.getIn(['pivot', 'company'])) ?? '';

      const prefix = getOptionValue(contact.get('prefix'));
      const suffix = getOptionValue(contact.get('suffix'));
      const body = { prefix, suffix };

      copySaved(body, saved, isAdvisor);
      handleAgeHelper(body);
      handleOwnershipPercentHelper(body);
      if (handleValidate(body)) {
        if (parseInt(body.age, 10) === 0) {
          delete body.age;
          delete body.birthYear;
        }
        saveCompanyContact({ companyId, contactId, body, saved }, () => {
          context.closePopup();
        });
      }
    }
  }, [
    companyId,
    contact,
    context,
    handleAgeHelper,
    handleOwnershipPercentHelper,
    handleValidate,
    inputErrors,
    saveCompanyContact,
  ]);

  const handleFetchTitles = useCallback(
    ({ value }) => {
      findTitles({
        filter: value,
        prefix: pathPrefix.slice(0, -1),
      });
    },
    [findTitles, pathPrefix],
  );

  const handleUpdateTitle = useCallback(
    param => {
      if ('text' in param) {
        const name = 'pivot.title';
        const value = param.text;

        handleUpdateCompany({
          name: `${pathPrefix}${name}`,
          value,
        });
      }

      if ('suggestions' in param) {
        const name = 'suggests';
        const value = param.suggestions;

        handleUpdateCompany({
          name: `${pathPrefix}${name}`,
          value,
        });
      }
    },
    [handleUpdateCompany, pathPrefix],
  );

  const handleSelectTitle = useCallback(
    (event, { suggestion }) => {
      handleUpdateCompany({
        name: `${pathPrefix}pivot.title`,
        value: suggestion.text,
      });
    },
    [handleUpdateCompany, pathPrefix],
  );

  const handleChangeSuggestion = useCallback(
    object => {
      if (typeof object.value === 'string') {
        const { value } = object;
        const nextValue = suffixesProp.find(
          suffix => suffix.get('name') === value,
          null,
          Map({
            name: value || '',
            value: value || '',
          }),
        );

        handleUpdateCompany({
          name: `${pathPrefix}${'suffix'}`,
          value: nextValue,
        });
      }
    },
    [handleUpdateCompany, pathPrefix, suffixesProp],
  );

  const titleValue = contact.getIn(['pivot', 'title'], '');
  const titleText = unwrap(titleValue);
  const titleProps = {
    onFetch: handleFetchTitles,
    suggestions: contact.getIn(['suggestions']),
    onUpdate: handleUpdateTitle,
    getValue: handleGetValue,
    onSelect: handleSelectTitle,
    value: titleValue,
    showExtraInfo: titleText && titleText.toUpperCase() === 'ADVISOR',
    loading,
  };

  const suffixValue = getIn(contactInfo, 'suffix').get('name');
  const idValue = suffixValue;
  const regExp = new RegExp(String(suffixValue).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1'), 'i');
  const suffixClassName = classNames('form-control input-sm', {
    changed: isDeepChanged(contactInfo.get('suffix')),
  });

  const suffixSuggestions = suffixesProp
    .filter(suffix => regExp.test(suffix.get('name')))
    .map(suffix => ({
      id: suffix.get('value'),
      text: suffix.get('name'),
    }));

  const suffixesSuggest = (
    <AutoComplete
      idValue={idValue}
      inputProps={{ className: suffixClassName }}
      loading={false}
      name="suffix"
      onChange={handleChangeSuggestion}
      onFind={args => {
        setTimeout(
          () =>
            args.afterSuccess({
              response: {
                data: suffixesProp
                  .filter(suffix => regExp.test(suffix.get('name')))
                  .map(suffix => ({
                    id: suffix.get('value'),
                    text: suffix.get('name'),
                  })),
              },
            }),
          0,
        );
      }}
      suggestions={suffixSuggestions}
      suggestionsName="suffixes"
      value={suffixValue}
      suggestIfEmpty
    />
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.EditContactPopup
          companyId={companyId}
          contact={contact}
          onChange={handleChange}
          onSave={handleSave}
          prefixes={prefixesProp}
          suffixes={suffixesProp}
          suffixesSuggest={suffixesSuggest}
          titleProps={titleProps}
          isValid
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  const openedContact = props.popup.getIn(['props', 'openedContact']);
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';
  const root = isBuyer ? state.targetCompany.buyer.marketing : state.targetCompany.target;
  const contact = root.getIn(['contacts', openedContact]);

  return {
    companyId: state.targetCompany.info.getIn(['info', 'id']),
    isValid: contact.get('isValid', true),
    inputErrors: contact.get('inputErrors', Map()),
    pathPrefix: `contacts.${openedContact}.`,
    prefixes,
    suffixes,
    contact,
    contactInfo: contact,
    loading: contact.get('loading'),
    user: state.auth.get('user', null),
  };
}

function mapDispatchToProps(dispatch, props) {
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';

  if (isBuyer) {
    const {
      handleUpdateCompany,
      saveCompanyContact,
      revertUpdate,
      closeValidationError,
      findTitles,
      addValidationError,
    } = BUYER;

    return bindActionCreators(
      {
        handleUpdateCompany,
        saveCompanyContact,
        revertUpdate,
        closeValidationError,
        findTitles,
        addValidationError,
      },
      dispatch,
    );
  }

  const {
    handleUpdateCompany,
    saveCompanyContact,
    revertUpdate,
    closeValidationError,
    findTitles,
    addValidationError,
  } = TARGET;

  return bindActionCreators(
    {
      handleUpdateCompany,
      saveCompanyContact,
      revertUpdate,
      closeValidationError,
      findTitles,
      addValidationError,
    },
    dispatch,
  );
}

export { EditContactPopupContainer };

const Connected = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(EditContactPopupContainer));

export default Connected;
